import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonService } from 'src/@services/common.service';
import { FormService } from 'src/@services/form.service';
import { CommonPagesService } from '../../common-pages.service';
import { ResponseStatus, ValidationMessage } from '../../enum';

/** Original display name of each system-defined role, keyed by its stable RoleCode. */
const SYSTEM_ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  ACCOUNTANT: 'Accountant',
  ACCOUNT_MANAGER: 'Account Manager',
  ACCOUNT_ADMIN: 'Account Admin',
  SITE_ADMIN: 'Site Admin',
  SITE_VIEWER: 'Site Viewer',
  SUPPLIER_ADMIN: 'Supplier Admin',
  SUPPLIER_FULL_ACCESS: 'Supplier Full Access',
  SUPPLIER_PARTIAL_ACCESS: 'Supplier Partial Access',
};

/** Fallback for codes added after this map: SOME_NEW_ROLE -> Some New Role. */
function labelFromRoleCode(roleCode: string): string {
  return roleCode
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

@Component({
  selector: 'app-add-update-role',
  templateUrl: './add-update-role.component.html',
  styleUrls: ['./add-update-role.component.scss'],

  standalone: false,
})
export class AddUpdateRoleComponent implements OnInit {
  isVisibleRoleDrawer = true;
  isLoadingOne = false;
  roleDrawerTitle = 'Add New Role';
  @Output() hideRoleDrawer = new EventEmitter();
  @Input() selectedRoleData: any;
  validationMessage = ValidationMessage;
  @Input() currentSelectedTab: any;
  /** Super Admin and Accountant names are referenced across auth paths, so the name field is locked for them. */
  isRoleNameLocked = false;
  /** Original name of a system-defined role, so a renamed role is still recognisable. Empty for custom roles. */
  _uiSystemRoleLabel = '';

  constructor(
    public _cS: CommonService,
    private _ws: CommonPagesService,
    public _fS: FormService
  ) {
    _fS.rolesForm_fb();
  }

  ngOnInit(): void {
    //patchvalue to form while edit
    if (this.selectedRoleData != null) {
      this.roleDrawerTitle = 'Edit Role (' + this.selectedRoleData.name + ')';
      this._fS.rolesForm.patchValue({
        roleName: this.selectedRoleData.name,
        description: this.selectedRoleData.description,
        isActiveRole: this.selectedRoleData.isActive,
        isBrooklynActive: this.selectedRoleData.isBrooklynActive,
      });

      const roleCode = (this.selectedRoleData?.roleCode || '').toUpperCase();
      const selectedName = (this.selectedRoleData?.name || '').toLowerCase();
      // Fall back to the display name for roles not yet backfilled with a RoleCode.
      this.isRoleNameLocked = roleCode
        ? roleCode === 'SUPER_ADMIN' || roleCode === 'ACCOUNTANT'
        : selectedName === 'accountant' || selectedName === 'super admin';

      this._uiSystemRoleLabel = roleCode
        ? SYSTEM_ROLE_LABELS[roleCode] ?? labelFromRoleCode(roleCode)
        : '';

      if (this.isRoleNameLocked) {
        this._fS.rolesForm.get('roleName')?.disable();
      } else {
        this._fS.rolesForm.get('roleName')?.enable();
      }
    }    
  }

  //cancel update role
  handleCancel() {
    this.isVisibleRoleDrawer = false;
    this.hideRoleDrawer.emit({ hide: this.isVisibleRoleDrawer, data: null });
  }

  //add/update role
  handleOk() {
    if (this._fS.rolesForm.valid) {
      // getRawValue so the locked role name is still submitted for rename-locked roles.
      const formValue = this._fS.rolesForm.getRawValue();
      var roleBody = {
        id: this.selectedRoleData != null ? this.selectedRoleData.id : '',
        name: formValue.roleName.trim(),
        description: formValue.description.trim(),
        isActive: formValue.isActiveRole,
        isBrooklynActive: formValue.isBrooklynActive,
        roleType: this.currentSelectedTab,
      };
      //add or edit unit data

      if (this.selectedRoleData != null) {
        this.isLoadingOne = false;
        this.updateRoleData(roleBody);
      } else {
        this.isLoadingOne = false;
        this.addRoleData(roleBody);
      }
    } else {
      //to fire validations on invalid fields
      this._cS.markFormGroupTouched(this._fS.rolesForm);
    }
  }

  //api call to add/update role
  addRoleData(roleBody: any) {
    this.isLoadingOne = true;
    this._ws.createRole(roleBody).subscribe(
      (response: any) => {
        if (response.responseStatus == ResponseStatus.success) {
          this._cS.displayMessage('success', response.message);

          //hide unit drawer
          this.isVisibleRoleDrawer = false;
          this.isLoadingOne = false;
          this.hideRoleDrawer.emit({
            hide: this.isVisibleRoleDrawer,
            data: response.result,
          });
        } else {
          this._cS.displayMessage('error', response.message);
          this.isLoadingOne = false;
        }
      },
      (error: any) => {
        // this._cS.displayMessage('error', error.message);
        this.isLoadingOne = false;
      }
    );
  }

  //api call to add/update role
  updateRoleData(roleBody: any) {
    this.isLoadingOne = true;
    this._ws.updateRole(roleBody).subscribe(
      (response: any) => {
        if (response.responseStatus == ResponseStatus.success) {
          this._cS.displayMessage('success', response.message);

          //hide unit drawer
          this.isVisibleRoleDrawer = false;
          this.isLoadingOne = false;
          this.hideRoleDrawer.emit({
            hide: this.isVisibleRoleDrawer,
            data: response.result,
          });
        } else {
          this._cS.displayMessage('error', response.message);
          this.isLoadingOne = false;
        }
      },
      (error: any) => {
        // this._cS.displayMessage('error', error.message);
        this.isLoadingOne = false;
      }
    );
  }
}
